import { lazy, Suspense } from "react";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Loader2 } from "lucide-react";
import SyncProvider from "@/components/SyncProvider";

// Lazy load toast components since they're not needed immediately
const Toaster = lazy(() => import("@/components/ui/toaster").then(m => ({ default: m.Toaster })));
const Sonner = lazy(() => import("@/components/ui/sonner").then(m => ({ default: m.Toaster })));

// Eager load only the most critical landing page
import Index from "./pages/Index";

// Lazy load WishlistDrawer since it's a modal not immediately visible
const WishlistDrawer = lazy(() => import("@/components/WishlistDrawer"));

// Lazy load all other pages for better initial bundle size
const ProductPage = lazy(() => import("./pages/ProductPage"));
const ModelsPage = lazy(() => import("./pages/ModelsPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const TermsPage = lazy(() => import("./pages/TermsPage"));
const PrivacyPage = lazy(() => import("./pages/PrivacyPage"));
const ShippingPage = lazy(() => import("./pages/ShippingPage"));
const ProductRedirect = lazy(() => import("./pages/ProductRedirect"));
const CollectionPage = lazy(() => import("./pages/CollectionPage"));
const CollectionsHubPage = lazy(() => import("./pages/CollectionsHubPage"));
const FAQPage = lazy(() => import("./pages/FAQPage"));
const CheckoutSuccessPage = lazy(() => import("./pages/CheckoutSuccessPage"));
const AuthPage = lazy(() => import("./pages/AuthPage"));
const OrderHistoryPage = lazy(() => import("./pages/OrderHistoryPage"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <Loader2 className="w-8 h-8 animate-spin text-primary" />
  </div>
);

// Configure QueryClient with optimized settings
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 30, // 30 minutes
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Suspense fallback={null}>
        <Toaster />
      </Suspense>
      <Suspense fallback={null}>
        <Sonner />
      </Suspense>
      <BrowserRouter>
        <SyncProvider>
          {/* WishlistDrawer lazy-loaded with its own Suspense to avoid blocking initial render */}
          <Suspense fallback={null}>
            <WishlistDrawer />
          </Suspense>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/shipping" element={<ShippingPage />} />
              <Route path="/product" element={<ProductRedirect />} />
              <Route path="/product/:slug" element={<ProductPage />} />
              <Route path="/models" element={<ModelsPage />} />
              <Route path="/collections" element={<CollectionsHubPage />} />
              <Route path="/collections/:category" element={<CollectionPage />} />
              <Route path="/faq" element={<FAQPage />} />
              <Route path="/auth" element={<AuthPage />} />
              <Route path="/orders" element={<OrderHistoryPage />} />
              <Route path="/admin" element={<AdminPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/checkout-success" element={<CheckoutSuccessPage />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </SyncProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
